import { create } from "zustand";
import { immer } from "zustand/middleware/immer";

type Sheet = {
  open: boolean;
  setOpen: (open: boolean) => void;
};

type PanelHandle = {
  isDragging: boolean;
  setDragging: (dragging: boolean) => void;
};

type Panel = {
  size: number;
  setSize: (size: number) => void;
  handle: PanelHandle;
};

export type DocumentType = "resume" | "cover-letter";

export type BuilderTab = "fill-in" | "design" | "analysis" | "cover-letter" | "roast";

type BuilderState = {
  frame: {
    ref: HTMLIFrameElement | null;
    setRef: (ref: HTMLIFrameElement | null) => void;
  };
  sheet: {
    left: Sheet;
    right: Sheet;
  };
  panel: {
    left: Panel;
    right: Panel;
  };
  leftPanelMode: "content" | "design";
  setLeftPanelMode: (mode: "content" | "design") => void;
  // New state for tabs
  activeTab: BuilderTab;
  setActiveTab: (tab: BuilderTab) => void;
  // New state for document type
  activeDocument: DocumentType;
  setActiveDocument: (doc: DocumentType) => void;
  // New state for cover letter modal
  showCoverLetterModal: boolean;
  setShowCoverLetterModal: (show: boolean) => void;
  // New state for autosave status
  isSaving: boolean;
  setIsSaving: (isSaving: boolean) => void;
  // New state for artboard loading
  isArtboardLoading: boolean;
  setIsArtboardLoading: (isLoading: boolean) => void;
  // Collapse all sections
  sectionsCollapsed: boolean;
  toggleCollapseAllSections: () => void;
  // Active section in the new section-rail navigation
  activeSectionId: string;
  setActiveSectionId: (id: string) => void;
};

type BuilderActions = {
  toggle: (side: "left" | "right") => void;
};

export const useBuilderStore = create<BuilderState & BuilderActions>()(
  immer((set) => ({
    frame: {
      ref: null,
      setRef: (ref) => {
        set((state) => {
          // @ts-expect-error Unable to set ref type
          state.frame.ref = ref;
        });
      },
    },
    sheet: {
      left: {
        open: false,
        setOpen: (open) => {
          set((state) => {
            state.sheet.left.open = open;
          });
        },
      },
      right: {
        open: false,
        setOpen: (open) => {
          set((state) => {
            state.sheet.right.open = open;
          });
        },
      },
    },
    panel: {
      left: {
        size: 0,
        setSize: (size) => {
          set((state) => {
            state.panel.left.size = size;
          });
        },
        handle: {
          isDragging: false,
          setDragging: (dragging) => {
            set((state) => {
              state.panel.left.handle.isDragging = dragging;
            });
          },
        },
      },
      right: {
        size: 0,
        setSize: (size) => {
          set((state) => {
            state.panel.right.size = size;
          });
        },
        handle: {
          isDragging: false,
          setDragging: (dragging) => {
            set((state) => {
              state.panel.right.handle.isDragging = dragging;
            });
          },
        },
      },
    },
    leftPanelMode: "content",
    setLeftPanelMode: (mode) => {
      set((state) => {
        state.leftPanelMode = mode;
      });
    },
    activeTab: "fill-in",
    setActiveTab: (tab) => {
      set((state) => {
        state.activeTab = tab;
      });
    },
    activeDocument: "resume",
    setActiveDocument: (doc) => {
      set((state) => {
        state.activeDocument = doc;
      });
    },
    showCoverLetterModal: false,
    setShowCoverLetterModal: (show) => {
      set((state) => {
        state.showCoverLetterModal = show;
      });
    },
    isSaving: false,
    setIsSaving: (isSaving) => {
      set((state) => {
        state.isSaving = isSaving;
      });
    },
    isArtboardLoading: true,
    setIsArtboardLoading: (isLoading) => {
      set((state) => {
        state.isArtboardLoading = isLoading;
      });
    },
    sectionsCollapsed: false,
    toggleCollapseAllSections: () => {
      set((state) => {
        state.sectionsCollapsed = !state.sectionsCollapsed;
      });
    },
    activeSectionId: "basics",
    setActiveSectionId: (id) => {
      set((state) => {
        state.activeSectionId = id;
      });
    },
    toggle: (side) => {
      set((state) => {
        state.sheet[side].open = !state.sheet[side].open;
      });
    },
  })),
);
